import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { ListPayload } from 'core/http/response';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import Notification from 'components/ui/base/notification/Notification';
import { listIcs2Arrivals } from 'store/declarations/eu/ics2/arrival/client';
import {
    ICS2ArrivalStatus,
    ICS2ArrivalView,
} from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { getErrorMessage } from 'utils/getErrorMessage';
import { useHandleTableChange } from 'utils/tableHelpers';

export const ARRIVAL_FILTER_KEYS = ['status', 'lrn', 'relatedMrn', 'arrivalDate'] as const;

export type ArrivalFilterKey = typeof ARRIVAL_FILTER_KEYS[number];

export type ArrivalFilters = Partial<Record<ArrivalFilterKey, string>>;

export const ARRIVAL_DATE_FORMAT = 'YYYY-MM-DD';

/** The arrival day is kept in the URL, while the service compares a range of instants with the arrival. */
const toStartOfDay = (date: string | undefined) => (date ? moment(date).startOf('day').toISOString() : undefined);
const toEndOfDay = (date: string | undefined) => (date ? moment(date).endOf('day').toISOString() : undefined);

const useArrivalNotificationsList = () => {
    const [arrivals, setArrivals] = useState<ListPayload<ICS2ArrivalView>>();
    const [isLoading, setIsLoading] = useState(false);

    const {
        query,
        filters,
        page,
        pageSize,
        searchParams,
        onSearch,
        setFilter,
        clearFilter,
        clearAllFilters,
        onTableChange,
    } = useHandleTableChange({ filterKeys: ARRIVAL_FILTER_KEYS });

    const filtersLoaded = filters !== null;
    const arrivalMrn = searchParams.get('query') ?? undefined;
    const status: ICS2ArrivalStatus | undefined = filters?.status ?? undefined;
    const lrn: string | undefined = filters?.lrn ?? undefined;
    const relatedMrn: string | undefined = filters?.relatedMrn ?? undefined;
    const arrivalDate: string | undefined = filters?.arrivalDate ?? undefined;

    useEffect(() => {
        if (!filtersLoaded) return;

        const controller = new AbortController();

        setIsLoading(true);
        listIcs2Arrivals({
            page,
            size: pageSize,
            status,
            lrn,
            arrivalMrn,
            relatedMrn,
            from: toStartOfDay(arrivalDate),
            to: toEndOfDay(arrivalDate),
            signal: controller.signal,
        })
            .then(setArrivals)
            .catch((error) => {
                if (axios.isCancel(error) || controller.signal.aborted) return;

                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Could not load arrival notifications.',
                    description: getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE,
                });
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoading(false);
            });

        return () => controller.abort();
    }, [filtersLoaded, page, pageSize, status, lrn, arrivalMrn, relatedMrn, arrivalDate]);

    const appliedFilters: Record<ArrivalFilterKey, string | undefined> = { status, lrn, relatedMrn, arrivalDate };

    const applyFilter = (key: ArrivalFilterKey, value: string | undefined) => {
        setFilter(key, value);
        onTableChange({ current: 1, pageSize });
    };

    const removeFilter = (key: ArrivalFilterKey) => {
        clearFilter(key);
        onTableChange({ current: 1, pageSize });
    };

    const clearFilters = () => {
        clearAllFilters();
        onTableChange({ current: 1, pageSize });
    };

    return {
        arrivals,
        isLoading,
        pageSize,
        mrnSearch: query.current ?? '',
        onMrnSearch: onSearch,
        filters: appliedFilters,
        applyFilter,
        removeFilter,
        clearFilters,
        onTableChange,
    };
};

export default useArrivalNotificationsList;
